import type { GetContractResult } from "@wagmi/core";

import MarginlyPool from "../abi/MarginlyPool";

type MPAbi = typeof MarginlyPool;
export type MarginlyPoolContract = GetContractResult<MPAbi>;

/**
 *
 * @param address
 * @param chainId
 * @returns apply convertBasePrice to useContractRead result
 */
export const getBasePriceSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "getBasePrice",
  } as const);

export const baseTokenSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "baseToken",
  } as const);

export const quoteTokenSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "quoteToken",
  } as const);

export const feeHolderSelector = (
  address?: `0x${string}`,
  /** @deprecated do not need chainId selector cause of validation wrapper */
  chainId?: number,
) =>
  ({
    abi: MarginlyPool,
    address,
    chainId,
    functionName: "feeHolder",
  } as const);

export const transformMarginlyParams = (
  params?: [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    bigint,
    bigint,
    bigint,
  ],
) => {
  if (!params) {
    return undefined;
  }

  const [
    maxLeverage,
    priceSecondsAgo,
    interestRate,
    fee,
    swapFee,
    positionSlippage,
    mcSlippage,
    positionMinAmount,
    baseLimit,
    quoteLimit,
  ] = params;

  return {
    maxLeverage,
    priceSecondsAgo,
    interestRate,
    fee,
    swapFee,
    positionSlippage,
    mcSlippage,
    positionMinAmount,
    baseLimit,
    quoteLimit,
  };
};

export const marginlyParamsSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "params",
  } as const);

export const discountedBaseCollateralSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "discountedBaseCollateralSelector",
  } as const);

export const discountedBaseDebtSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "discountedBaseDebtSelector",
  } as const);

export const discountedQuoteCollateralSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "discountedQuoteCollateralSelector",
  } as const);

export const discountedQuoteDebtSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "discountedQuoteDebtSelector",
  } as const);

export const lastReinitTimestampSecondsSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "lastReinitTimestampSeconds",
  } as const);

/**
 *
 * @param address
 * @returns apply convertFP96 to useContractRead result
 */
export const baseCollateralCoeffSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "baseCollateralCoeff",
  } as const);

/**
 *
 * @param address
 * @returns apply convertFP96 to useContractRead result
 */
export const baseDebtCoeffSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "baseDebtCoeff",
  } as const);

/**
 *
 * @param address
 * @returns apply convertFP96 to useContractRead result
 */
export const quoteCollateralCoeffSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "quoteCollateralCoeff",
  } as const);

/**
 *
 * @param address
 * @returns apply convertFP96 to useContractRead result
 */
export const quoteDebtCoeffSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "quoteDebtCoeff",
  } as const);

/**
 *
 * @param address
 * @returns apply convertSystemLeverage to useContractRead result
 */
export const systemLeverageSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "systemLeverage",
  } as const);

/**
 *
 * @param address
 * @param who
 * @returns apply convertPosition to useContractRead result
 */
export const positionsSelector = (
  address?: `0x${string}`,
  who?: `0x${string}`,
) =>
  who
    ? ({
        abi: MarginlyPool,
        address,
        functionName: "positions",
        args: [who],
      } as const)
    : ({
        abi: MarginlyPool,
        address,
        functionName: "positions",
      } as const);

/**
 *
 * @param address
 * @param who
 * @returns apply convertWorkingMode to useContractRead result
 */
export const workingModeSelector = (address?: `0x${string}`) =>
  ({
    address,
    functionName: "mode",
  } as const);

/**
 *
 * @param address
 * @returns apply convertFP96 to useContractRead result
 */
export const emergencyWithdrawCoeffSelector = (address?: `0x${string}`) =>
  ({
    abi: MarginlyPool,
    address,
    functionName: "emergencyWithdrawCoeff",
  } as const);
